import { Capacitor } from '@capacitor/core';
import { Directive, inject } from '@angular/core';
import { Events } from 'src/app/@core/event.service';
import { Keyboard, KeyboardInfo } from '@capacitor/keyboard';

export const DEFAULT_KEYBOARD_HEIGHT = 360;

// animation duration should match with any related css transition duration (e.g. message-footer)
const ANDROID_KEYBOARD_ANIMATION_DURATION = 300; // TODO: check if this value is actually true for android devices
const IOS_KEYBOARD_ANIMATION_DURATION = 500;

@Directive({
  selector: '[handle-keyboard-events]',
  standalone: false,
})
export class HandleKeyboardEventsDirective {
  private keyboardHeight: number;
  private animationDuration: number;
  private animationStartTime: number;
  private animationTimeout: ReturnType<typeof setTimeout>;

  private keyboardIsVisible: boolean;

  private events = inject(Events);

  public ngOnInit(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    Keyboard.addListener('keyboardWillShow', this.onKeyboardWillShow.bind(this));
    Keyboard.addListener('keyboardDidShow', this.onKeyboardDidShow.bind(this));
    Keyboard.addListener('keyboardWillHide', this.onKeyboardWillHide.bind(this));
    Keyboard.addListener('keyboardDidHide', this.onKeyboardDidHide.bind(this));

    this.animationDuration = Capacitor.getPlatform() === 'ios'
      ? IOS_KEYBOARD_ANIMATION_DURATION
      : ANDROID_KEYBOARD_ANIMATION_DURATION;
  }

  public ngOnDestroy(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    Keyboard.removeAllListeners();
    this.events.unsubscribe('keyboardWillShow');
    this.events.unsubscribe('keyboardDidShow');
    this.events.unsubscribe('keyboardWillHide');
    this.events.unsubscribe('keyboardDidHide');
  }

  private onKeyboardWillShow(info: KeyboardInfo): void {
    this.animationStartTime = Date.now();

    document.body.classList.remove('keyboard-did-hide');
    document.body.classList.add('keyboard-will-show');
    this.keyboardHeight = info.keyboardHeight <= 0
      ? DEFAULT_KEYBOARD_HEIGHT
      : info.keyboardHeight;
    document.documentElement.style.setProperty('--keyboard-height', `${this.keyboardHeight}px`);
    this.events.publish('keyboardWillShow', {
      keyboardHeight: this.keyboardHeight,
    });
  }

  // android seems to fire didShow/didHide event too early, so we always make sure
  // it takes at least `this.animationDuration` before updating css-related stuff
  // Special case: we don't want to wait for animationDuration when keyboard layout has been changed
  // while keyboard is already open (this also will fire willShow + didShow)
  private onKeyboardDidShow(): void {
    const diff = this.keyboardIsVisible
      ? this.animationDuration
      : Date.now() - this.animationStartTime; // in ms
    const t = diff < this.animationDuration
      ? this.animationDuration - diff
      : 1;

    clearTimeout(this.animationTimeout);
    this.animationTimeout = setTimeout(() => {
      document.body.classList.remove('keyboard-will-show');
      document.body.classList.add('keyboard-did-show');
      this.events.publish('keyboardDidShow', {
        keyboardHeight: this.keyboardHeight,
      });
    }, t);

    this.keyboardIsVisible = true;
  }

  private onKeyboardWillHide(): void {
    this.animationStartTime = Date.now();
    this.events.publish('keyboardWillHide', {
      keyboardHeight: this.keyboardHeight,
    });

    document.body.classList.remove('keyboard-will-show', 'keyboard-did-show');
    document.body.classList.add('keyboard-will-hide');
    this.keyboardIsVisible = false;
  }

  private onKeyboardDidHide(): void {
    const diff = Date.now() - this.animationStartTime; // in ms
    const t = diff < this.animationDuration
      ? this.animationDuration - diff
      : 1;

    clearTimeout(this.animationTimeout);
    this.animationTimeout = setTimeout(() => {
      document.body.classList.remove('keyboard-will-hide');
      document.body.classList.add('keyboard-did-hide');
      this.events.publish('keyboardDidHide', {
        keyboardHeight: this.keyboardHeight,
      });
    }, t);
  }
}
